import { FormattedDate, FormattedMessage, FormattedNumber } from 'components/shared/Intl';
import { LearnMoreIcon } from 'components/shared/LearnMore/LearnMoreIcon';
import { IntlKey } from 'locales';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { Underlying } from 'models/underlying';
import type {
  InstrumentName,
  ProductType,
  QuoteDetailsData,
  QuoteDetailsField,
  SolvingModeDetail,
} from 'state/quotes/details/details.model';
import { isAmount } from 'utils/streaming/quotePriceEvent';

import { UnderlyingBadgesList } from '../UnderlyingBadge';

interface FieldValueProps {
  field: QuoteDetailsField;
  value: QuoteDetailsData[QuoteDetailsField];
  productType: ProductType | undefined;
  instrumentName?: InstrumentName | null;
  solvingMode?: SolvingModeDetail | null;
}

export function isProductWithStrikeInPercent(
  type: ProductType | null | undefined,
  instrumentName?: InstrumentName | null,
) {
  return type !== 'FxDualCurrency' && instrumentName !== 'FixedLegWithFxOption';
}

export function FieldValue({ field, value, productType, instrumentName, solvingMode }: FieldValueProps) {
  switch (field) {
    case 'Underlyings':
      return <UnderlyingBadgesList underlyings={value as ReadonlyArray<Underlying>} />;

    case 'MaturityDate':
      return <FormattedDate value={value as string} year="numeric" month="short" day="2-digit" />;

    case 'NotionalAmount':
      return <FormattedNumber value={value as number} />;

    case 'SelectedOnBehalfClient':
      const client = value as Readonly<ISalesCounterpart>;
      return (
        <span className="fw-bold" data-e2e={field + '-value'} title={client.label}>
          {client.label}
        </span>
      );

    case 'KIThreshold':
    case 'RecallThreshold':
    case 'UpfrontFee':
    case 'XMargin':
    case 'TradingPrice':
      return (
        <span data-e2e={field + '-value'}>
          <>{value} %</>
        </span>
      );
    case 'Strike':
      return (
        <span data-e2e={field + '-value'}>
          <>
            {value}
            {isProductWithStrikeInPercent(productType, instrumentName) ? ' %' : ''}
          </>
        </span>
      );

    case 'RecallCoupon':
      return (
        <span data-e2e={field + '-value'}>
          <>
            {value} % <FormattedMessage id="field.Flat" defaultMessage="Per period" />
          </>
        </span>
      );

    case 'CouponFrequency':
      const frequency = value as string;
      return <FormattedMessage id={`field.${frequency}` as IntlKey} defaultMessage={frequency} />;

    case 'SolvingMode':
      const solvingModeDetail = value as SolvingModeDetail;
      const solveFor = solvingModeDetail.label ?? solvingModeDetail.id;
      return <FormattedMessage id={`field.${solveFor}` as IntlKey} defaultMessage={solveFor} />;

    case 'SolvedParamSensi':
      const sensiValue = value as number;
      const sensiSolveFor = solvingMode?.label ?? solvingMode?.id;
      return (
        <span data-e2e="SolvedParamSensi-value">
          <FormattedNumber value={sensiValue} maximumFractionDigits={4} unit="%" />
          {' UF for 1% '}
          <FormattedMessage id={`field.${sensiSolveFor}` as IntlKey} defaultMessage={sensiSolveFor} />
        </span>
      );
    case 'ProductSubtype':
      if (value === '') {
        return <span className="text-muted">_</span>;
      }
      return (
        <div className="d-flex gap-1" data-e2e="DocumentationTitle-value">
          <>
            {typeof value === 'string' ? (
              <FormattedMessage id={`field.${value}` as IntlKey} defaultMessage={value} />
            ) : (
              value
            )}
          </>
          {typeof value === 'string' && <LearnMoreIcon productType={productType} productSubType={value} />}
        </div>
      );
    case 'Wrapper':
      return (
        <span data-e2e={field + '-value'}>
          <>
            {typeof value === 'string' ? (
              <FormattedMessage id={`field.${value}` as IntlKey} defaultMessage={value} />
            ) : (
              value
            )}
          </>
        </span>
      );
    default:
      const fieldValue = fieldByValuePredicate(field, value);
      return (
        fieldValue ?? (
          <span data-e2e={field + '-value'}>
            <>{typeof value === 'object' ? '' : value}</>
          </span>
        )
      );
  }
}

function fieldByValuePredicate(field: string, value: unknown) {
  if (isAmount(value)) {
    return (
      <span data-e2e={field + '-value'}>
        <FormattedNumber value={value.value} /> {value.unit}
      </span>
    );
  }
}
